import { IMG } from "@/data/site";
import FinalCTA from "@/components/FinalCTA";
import { ButtonLink, Eyebrow, FadeIn, PageHero, usePageMeta } from "@/components/blocks";

const FOCUS = [
  {
    n: "01",
    title: "Injury Rehabilitation Support",
    copy: "Programming that works alongside your physiotherapist’s guidance — rebuilding capacity without rushing the process.",
  },
  {
    n: "02",
    title: "Office-Related Pain",
    copy: "Back, neck and shoulder discomfort built over years of desk work, addressed through targeted strength and mobility.",
  },
  {
    n: "03",
    title: "Mobility & Movement Quality",
    copy: "Restrictions and weak patterns identified in assessment, then trained out of the system.",
  },
  {
    n: "04",
    title: "Return-to-Training",
    copy: "A structured bridge from pain or a long layoff back to real, progressive training.",
  },
];

export default function Recovery() {
  usePageMeta(
    "Recovery & Rehabilitation Coaching | StrengthLeap",
    "Pain management, rehabilitation support, mobility and return-to-training coaching. Recovery isn’t the opposite of performance — it’s part of it."
  );

  return (
    <main>
      <PageHero
        testid="recovery-page"
        eyebrow="Recovery"
        lines={["Recovery isn’t the", "opposite of performance."]}
        copy="It’s part of it. StrengthLeap restores pain-free movement through live, guided 1:1 video sessions — the foundation everything else is built on."
      />

      <section className="bg-bone px-6 py-28 text-ink sm:px-10 lg:px-16">
        <div className="mx-auto grid max-w-[1440px] gap-16 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
          <div>
            <FadeIn>
              <Eyebrow tone="ink">The Principle</Eyebrow>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-8 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl">
                Pain is information. <span className="text-ink/40">We read it.</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-smoke sm:text-lg">
                Many people enter StrengthLeap because something hurts. The path from there is the system: restore
                movement, build strength, then raise the ceiling on what your body can do.
              </p>
            </FadeIn>
            <FadeIn delay={0.25}>
              <p className="mt-10 font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">
                Recover <span className="text-lime">→</span> Build <span className="text-lime">→</span> Perform
              </p>
            </FadeIn>
            <FadeIn delay={0.3} className="mt-10">
              <ButtonLink to="/start" variant="ink" testid="recovery-cta-assessment">
                Start Your Assessment
              </ButtonLink>
            </FadeIn>
          </div>

          <div className="overflow-hidden">
            <div className="aspect-[4/5] overflow-hidden bg-ink/5">
              <img
                src={IMG.ice}
                alt="Athlete in cold-water recovery"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ink px-6 py-28 text-bone sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1440px]">
          <FadeIn>
            <Eyebrow>Where We Help</Eyebrow>
          </FadeIn>
          <div className="mt-12">
            {FOCUS.map((f) => (
              <FadeIn key={f.n}>
                <div
                  data-testid={`recovery-focus-${f.n}`}
                  className="grid gap-4 border-t border-bone/15 py-10 last:border-b sm:grid-cols-[100px_1fr_1.2fr] sm:items-baseline sm:gap-10"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.3em] text-lime">{f.n}</p>
                  <h3 className="font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">{f.title}</h3>
                  <p className="max-w-lg text-base leading-relaxed text-ash">{f.copy}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.1}>
            <p className="mt-12 max-w-2xl font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-ash">
              StrengthLeap provides coaching support, not medical treatment. We work alongside — never in place of —
              qualified medical care.
            </p>
          </FadeIn>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
